/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningHero.module.scss';

const START_COUNT = 600000000;

const FILTER_ROWS = [
  { key: 'jurisdiction', label: 'Jurisdiction', value: 'United Kingdom, Ireland, Nordics', count: 8940000 },
  { key: 'activity', label: 'Activity', value: 'Precision engineering & components', count: 61300 },
  { key: 'turnover', label: 'Turnover', value: '£20M – £60M, last filed accounts', count: 2480 },
  { key: 'headcount', label: 'Headcount', value: '50 – 400 employees', count: 1910 },
  { key: 'ebitda', label: 'EBITDA', value: '£3M – £10M, derived from filed accounts', count: 1240 },
  { key: 'ownership', label: 'Ownership', value: 'Majority privately held, no sponsor on register', count: 380 },
  { key: 'screening', label: 'Screening', value: 'No sanctions or PEP match, officers and owners', count: 41 },
];

const COUNTS = [START_COUNT, ...FILTER_ROWS.map((row) => row.count)];

const KPIS = [
  { value: '600M+', label: 'company profiles' },
  { value: '400+', label: 'primary registries' },
  { value: '200+', label: 'countries' },
  { value: '147', label: 'documented fields' },
  { value: 'Daily', label: 'registry sync' },
];

const SHORTLIST = [
  {
    name: 'Kestrel Precision Holdings Ltd',
    registry: 'GB 08812440',
    turnover: '£41.8M',
    staff: '214',
    control: 'Family and trust',
  },
  {
    name: 'Nordhavn Komponenter AS',
    registry: 'NO 918 442 601',
    turnover: '£38.1M',
    staff: '176',
    control: 'Founder-held',
  },
  { name: 'Vantage Forge Ltd', registry: 'GB 07741209', turnover: '£33.6M', staff: '158', control: 'Founder-held' },
  { name: 'Brixen Tooling AB', registry: 'SE 556781-4420', turnover: '£29.4M', staff: '131', control: 'Family' },
  { name: 'Pallas Toolworks Ltd', registry: 'GB 09220144', turnover: '£26.9M', staff: '118', control: 'Management' },
  { name: 'Halden Presisjon AS', registry: 'NO 921 006 338', turnover: '£24.2M', staff: '96', control: 'Founder-held' },
];

const fmt = (n: number) => n.toLocaleString('en-GB');

export const InvestmentScreeningHero = () => {
  const { t } = useInt();
  const proofRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [activeRow, setActiveRow] = useState(-1);
  const [count, setCount] = useState(START_COUNT);
  const [narrow, setNarrow] = useState(false);
  const [listVisible, setListVisible] = useState(false);

  useEffect(() => {
    const node = proofRef.current;

    if (!node) {
      return undefined;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setActiveRow(FILTER_ROWS.length - 1);
      setCount(FILTER_ROWS[FILTER_ROWS.length - 1].count);
      setNarrow(true);
      setListVisible(true);
      return undefined;
    }

    let step = 0;

    const tween = (from: number, to: number, ms: number, done: () => void) => {
      const t0 = performance.now();

      const frame = (now: number) => {
        const progress = Math.min(1, (now - t0) / ms);
        const eased = 1 - (1 - progress) ** 3;

        setCount(Math.round(from + (to - from) * eased));

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(frame);
        } else {
          done();
        }
      };

      rafRef.current = requestAnimationFrame(frame);
    };

    const advance = () => {
      if (step >= FILTER_ROWS.length) {
        setListVisible(true);
        timeoutRef.current = setTimeout(() => {
          setActiveRow(-1);
          setListVisible(false);
          setNarrow(false);
          setCount(START_COUNT);
          step = 0;
          timeoutRef.current = setTimeout(advance, 700);
        }, 4200);
        return;
      }

      setActiveRow(step);
      tween(COUNTS[step], COUNTS[step + 1], 400, () => {
        step += 1;

        if (step >= 5) {
          setNarrow(true);
        }

        timeoutRef.current = setTimeout(advance, 340);
      });
    };

    let started = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            timeoutRef.current = setTimeout(advance, 380);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <Section className={styles.root}>
      <div className={styles.headWrap}>
        <TitleBlock
          size="page"
          title={t('Screen 600 million companies down to the twelve worth a call.')}
          description={t(
            'Filed accounts, shareholder registers, deal history and officer records taken directly from 400+ government registries across 200+ countries. Every field on the shortlist carries the filing it came from.',
          )}
        />
        <div className={styles.actions}>
          <Button type="blue" size="xxl" href="/book-a-demo">
            {t('Compare it to your current source')}
          </Button>
        </div>
      </div>

      <div className={styles.wide}>
        <div className={styles.proof} ref={proofRef}>
          <div className={styles.kpis}>
            {KPIS.map((kpi) => (
              <div key={kpi.label} className={styles.kpi}>
                <b>{kpi.value}</b>
                <span>{t(kpi.label)}</span>
              </div>
            ))}
          </div>

          <div className={styles.proofBody}>
            <div className={styles.proofL}>
              <div className={styles.proofH}>
                <div>
                  <div className={styles.t}>{t('Target universe')}</div>
                  <div className={cn(styles.scCount, narrow && styles.scCountNarrow)}>{fmt(count)}</div>
                </div>
                <div className={styles.proofHNote}>
                  {t('companies')}
                  <br />
                  {t('matching')}
                </div>
              </div>

              <div>
                {FILTER_ROWS.map((row, i) => (
                  <div key={row.key} className={cn(styles.frow, activeRow === i && styles.frowOn)}>
                    <div className={styles.fk}>{t(row.label)}</div>
                    <div className={styles.fv}>{row.value}</div>
                    <div className={styles.fn}>{fmt(row.count)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.proofR}>
              <div className={styles.proofH}>
                <div className={styles.t}>{t('Shortlist — sample of 41')}</div>
                <div className={styles.proofHMono}>{t('SORTED BY TURNOVER, FY2025')}</div>
              </div>

              <table className={styles.sl}>
                <thead>
                  <tr>
                    <th>{t('Company')}</th>
                    <th>{t('Registry')}</th>
                    <th>{t('Turnover')}</th>
                    <th>{t('Staff')}</th>
                    <th>{t('Control')}</th>
                  </tr>
                </thead>
                <tbody>
                  {SHORTLIST.map((row, i) => (
                    <tr
                      key={row.name}
                      className={cn(styles.slRow, listVisible && styles.slRowIn)}
                      style={{ transitionDelay: `${i * 65}ms` }}
                    >
                      <td className={styles.nm}>{row.name}</td>
                      <td className={styles.sub}>{row.registry}</td>
                      <td className={styles.num}>{row.turnover}</td>
                      <td className={styles.num}>{row.staff}</td>
                      <td>{row.control}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.proofFoot}>
            <span>{t('Illustrative screen and sample entities. Counts refresh daily.')}</span>
            <span className={styles.proofFootMono}>EXPORT · CSV / API / WAREHOUSE</span>
          </div>
        </div>
      </div>
    </Section>
  );
};
